import {
  SignalStoreFeature,
  patchState,
  signalStoreFeature,
  withComputed,
  withState,
} from '@ngrx/signals';
import { Signal, computed, inject } from '@angular/core';
import { WritableStateSource } from '@ngrx/signals';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import {
  IErrorStateWithCorrelation,
  IRequestCorrelationState,
  projectDetailActions,
} from '@core/actions/project-detail.actions';
import { filter, firstValueFrom } from 'rxjs';

import { mapFn, SimpleActionCreator } from '@core/actions/util';

export const RequestStatus = {
  idle: 'idle',
  pending: 'pending',
  success: 'success',
  error: 'error',
} as const;
export type RequestStatus = (typeof RequestStatus)[keyof typeof RequestStatus];
type Empty = {};
interface IdleRequestState {
  status: typeof RequestStatus.idle;
}
interface PendingRequestState<T = any> {
  status: typeof RequestStatus.pending;
  metadata: T;
}
interface SuccessRequestState<T = any> {
  status: typeof RequestStatus.success;
  metadata: T;
}
interface ErrorRequestState {
  status: typeof RequestStatus.error;
  error: string;
}

export type RequestState<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
> =
  | IdleRequestState
  | PendingRequestState<TPendingMetadata>
  | SuccessRequestState<TSuccessMetadata>
  | ErrorRequestState;

export const idleRequestState: IdleRequestState = {
  status: RequestStatus.idle,
} as const;

export const createIdleRequestState = (): IdleRequestState => idleRequestState;
export const createPendingRequestState = <T = any>(
  metadata: T
): PendingRequestState => ({
  status: RequestStatus.pending,
  metadata,
});
export const createSuccessRequestState = <T = any>(
  metadata: T
): SuccessRequestState => ({
  status: RequestStatus.success,
  metadata,
});
export const createErrorRequestState = (error: string): ErrorRequestState => ({
  status: RequestStatus.error,
  error,
});

export type RequestStateSlice<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
> = {
  requestState: RequestState<TPendingMetadata, TSuccessMetadata>;
};

export type NamedRequestStateSlice<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
> = {
  [K in Collection as `${K}RequestState`]: RequestState<
    TPendingMetadata,
    TSuccessMetadata
  >;
};
export type RequestWritableStateSources<TPendingMetadata, TSuccessMetadata> = {
  status: Signal<RequestStatus>;
  isPending: Signal<boolean>;
  hasSuccess: Signal<boolean>;
  hasError: Signal<boolean>;
  error: Signal<string | null>;
  pendingMetadata: Signal<TPendingMetadata | null>;
  successMetadata: Signal<TSuccessMetadata | null>;
};

export type NamedRequestWritableStateSources<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
> = {
  [K in Collection as `${K}Status`]: Signal<RequestStatus>;
} & {
  [K in Collection as `${K}IsPending`]: Signal<boolean>;
} & {
  [K in Collection as `${K}HasSuccess`]: Signal<boolean>;
} & {
  [K in Collection as `${K}HasError`]: Signal<boolean>;
} & {
  [K in Collection as `${K}Error`]: Signal<string | null>;
} & {
  [K in Collection as `${K}PendingMetadata`]: Signal<TPendingMetadata | null>;
} & {
  [K in Collection as `${K}SuccessMetadata`]: Signal<TSuccessMetadata | null>;
};

export const getKeys = <Collection extends string>(config?: {
  collection: Collection;
}) => {
  const { collection } = config ?? {};
  return {
    stateKey: collection ? `${collection}RequestState` : 'requestState',
    statusKey: collection ? `${collection}Status` : 'status',
    isPendingKey: collection ? `${collection}IsPending` : 'isPending',
    hasSuccessKey: collection ? `${collection}HasSuccess` : 'hasSuccess',
    hasErrorKey: collection ? `${collection}HasError` : 'hasError',
    errorKey: collection ? `${collection}Error` : 'error',
    pendingMetadataKey: collection
      ? `${collection}PendingMetadata`
      : 'pendingMetadata',
    successMetadataKey: collection
      ? `${collection}SuccessMetadata`
      : 'successMetadata',
  };
};
type WithoutCollection<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
> = WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>;
type WithCollection<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
> = WritableStateSource<
  NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
>;

export function withRequestStateFeature<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(config: {
  collection: Collection;
}): SignalStoreFeature<
  { state: Empty; props: Empty; methods: Empty },
  {
    state: NamedRequestStateSlice<
      Collection,
      TPendingMetadata,
      TSuccessMetadata
    >;
    props: NamedRequestWritableStateSources<
      Collection,
      TPendingMetadata,
      TSuccessMetadata
    >;
    methods: Empty;
  }
>;

export function withRequestStateFeature<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(): SignalStoreFeature<
  { state: Empty; props: Empty; methods: Empty },
  {
    state: RequestStateSlice<TPendingMetadata, TSuccessMetadata>;
    props: RequestWritableStateSources<TPendingMetadata, TSuccessMetadata>;
    methods: Empty;
  }
>;
export function withRequestStateFeature<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(config?: { collection: Collection }): SignalStoreFeature {
  const {
    stateKey,
    statusKey,
    isPendingKey,
    hasSuccessKey,
    hasErrorKey,
    errorKey,
    pendingMetadataKey,
    successMetadataKey,
  } = getKeys(config);
  return signalStoreFeature(
    withState({
      [stateKey]: createIdleRequestState(),
    }),
    withComputed(
      (
        state:
          | WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>
          | WritableStateSource<
              NamedRequestStateSlice<
                Collection,
                TPendingMetadata,
                TSuccessMetadata
              >
            >
      ) => {
        const requestState = state[stateKey] as Signal<RequestState>;
        return {
          [statusKey]: computed(() => requestState().status),
          [isPendingKey]: computed(
            () => requestState().status === RequestStatus.pending
          ),
          [hasSuccessKey]: computed(
            () => requestState().status === RequestStatus.success
          ),
          [hasErrorKey]: computed(
            () => requestState().status === RequestStatus.error
          ),
          [errorKey]: computed(() => {
            const state = requestState();
            return state.status === RequestStatus.error ? state.error : null;
          }),
          [pendingMetadataKey]: computed(() => {
            const state = requestState();
            return state.status === RequestStatus.pending
              ? state.metadata
              : null;
          }),
          [successMetadataKey]: computed(() => {
            const state = requestState();
            return state.status === RequestStatus.success
              ? state.metadata
              : null;
          }),
        };
      }
    )
  );
}

export function setIdleRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<
    NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
  >,
  collection: Collection
): void;
export function setIdleRequestState<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>
): void;
export function setIdleRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state:
    | WritableStateSource<
        NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
      >
    | WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
  collection?: Collection
) {
  patchState(
    state as WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
    {
      [collection ? `${collection}RequestState` : 'requestState']:
        createIdleRequestState(),
    }
  );
}

export function setPendingRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<
    NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
  >,
  metadata: TPendingMetadata,
  collection: Collection
): void;
export function setPendingRequestState<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
  metadata: TPendingMetadata
): void;
export function setPendingRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state:
    | WritableStateSource<
        NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
      >
    | WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
  metadata: TPendingMetadata,
  collection?: Collection
) {
  patchState(
    state as WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
    {
      [collection ? `${collection}RequestState` : 'requestState']:
        createPendingRequestState(metadata),
    }
  );
}

export function setSuccessRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<
    NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
  >,
  metadata: TSuccessMetadata,
  collection: Collection
): void;
export function setSuccessRequestState<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
  metadata: TSuccessMetadata
): void;
export function setSuccessRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state:
    | WritableStateSource<
        NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
      >
    | WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
  metadata: TSuccessMetadata,
  collection?: Collection
) {
  patchState(
    state as WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
    {
      [collection ? `${collection}RequestState` : 'requestState']:
        createSuccessRequestState(metadata),
    }
  );
}

// export const setErrorRequestState = (
//   state: WritableStateSource<RequestStateSlice>,
//   error: string
// ) => {
//   patchState(state, { requestState: createErrorRequestState(error) });
// };
export function setErrorRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<
    NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
  >,
  error: string,
  collection: Collection
): void;
export function setErrorRequestState<
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state: WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
  error: string
): void;
export function setErrorRequestState<
  Collection extends string,
  TPendingMetadata = any,
  TSuccessMetadata = TPendingMetadata,
>(
  state:
    | WritableStateSource<
        NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
      >
    | WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
  error: string,
  collection?: Collection
) {
  patchState(
    state as WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>,
    {
      [collection ? `${collection}RequestState` : 'requestState']:
        createErrorRequestState(error),
    }
  );
}

type UnnamedRequestWritableStateSource<TPendingMetadata, TSuccessMetadata> =
  WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>;
type NamedRequestWritableStateSource<
  Collection extends string,
  TPendingMetadata,
  TSuccessMetadata,
> = WritableStateSource<
  NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
>;
type EitherRequestWritableStateSource<
  Collection extends string,
  TPendingMetadata,
  TSuccessMetadata,
> =
  | NamedRequestWritableStateSource<Collection, TPendingMetadata, TSuccessMetadata>
  | UnnamedRequestWritableStateSource<TPendingMetadata, TSuccessMetadata>;
const mapStateFnBuilder =
  <
    Collection extends string,
    TState extends EitherRequestWritableStateSource<
      Collection,
      TPendingMetadata,
      TSuccessMetadata
    >,
    TPendingMetadata = any,
    TSuccessMetadata = TPendingMetadata,
  >(
    state: TState,
    key: Collection | undefined
  ) =>
  (
    namedStateUpdate: (
      namedState: NamedRequestWritableStateSource<
        Collection,
        TPendingMetadata,
        TSuccessMetadata
      >,
      key: Collection
    ) => void,
    unnamedStateUpdate: (
      unnamedState: UnnamedRequestWritableStateSource<
        TPendingMetadata,
        TSuccessMetadata
      >
    ) => void
  ) =>
    key
      ? namedStateUpdate(
          state as NamedRequestWritableStateSource<
            Collection,
            TPendingMetadata,
            TSuccessMetadata
          >,
          key
        )
      : unnamedStateUpdate(
          state as UnnamedRequestWritableStateSource<TPendingMetadata, TSuccessMetadata>
        );

export const injectRequestBuilder = () => {
  const store = inject(Store);
  const actions = inject(Actions);

  async function startRequest<
    TSuccessActionType extends string,
    TErrorActionType extends string,
    TState extends WritableStateSource<
      RequestStateSlice<TPendingMetadata, TSuccessMetadata>
    >,
    TPendingMetadata = any,
    TSuccessMetadata = TPendingMetadata,
    TSuccessActionProps extends
      IRequestCorrelationState = IRequestCorrelationState,
    TErrorActionProps extends
      IErrorStateWithCorrelation = IErrorStateWithCorrelation,
  >(
    state: TState,
    requestAction: IRequestCorrelationState & Action,
    successActionCreator: SimpleActionCreator<
      TSuccessActionType,
      TSuccessActionProps
    >,
    errorActionCreator: SimpleActionCreator<
      TErrorActionType,
      TErrorActionProps
    >,
    collectionKey: undefined,
    getPendingMetadata: (
      requestAction?: IRequestCorrelationState
    ) => TPendingMetadata,
    getSuccessMetadata?: (
      successActionCreator: TSuccessActionProps
    ) => TSuccessMetadata,
    preDispatch?: () => void
  ): Promise<void>;
  async function startRequest<
    TSuccessActionType extends string,
    TErrorActionType extends string,
    Collection extends string,
    TState extends WritableStateSource<
      NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
    >,
    TPendingMetadata = any,
    TSuccessMetadata = TPendingMetadata,
    TSuccessActionProps extends
      IRequestCorrelationState = IRequestCorrelationState,
    TErrorActionProps extends
      IErrorStateWithCorrelation = IErrorStateWithCorrelation,
  >(
    state: TState,
    requestAction: IRequestCorrelationState & Action,

    successActionCreator: SimpleActionCreator<
      TSuccessActionType,
      TSuccessActionProps
    >,
    errorActionCreator: SimpleActionCreator<
      TErrorActionType,
      TErrorActionProps
    >,
    collectionKey: Collection,
    getPendingMetadata?: (
      requestAction: IRequestCorrelationState
    ) => TPendingMetadata,
    getSuccessMetadata?: (
      successActionCreator: TSuccessActionProps
    ) => TSuccessMetadata,
    preDispatch?: () => void
  ): Promise<void>;
  async function startRequest<
    TSuccessActionType extends string,
    TErrorActionType extends string,
    Collection extends string,
    TState extends
      | WritableStateSource<RequestStateSlice<TPendingMetadata, TSuccessMetadata>>
      | WritableStateSource<
          NamedRequestStateSlice<Collection, TPendingMetadata, TSuccessMetadata>
        >,
    TPendingMetadata = any,
    TSuccessMetadata = TPendingMetadata,
    TSuccessActionProps extends
      IRequestCorrelationState = IRequestCorrelationState,
    TErrorActionProps extends
      IErrorStateWithCorrelation = IErrorStateWithCorrelation,
  >(
    state: TState,
    requestAction: IRequestCorrelationState & Action,
    successActionCreator: SimpleActionCreator<
      TSuccessActionType,
      TSuccessActionProps
    >,
    errorActionCreator: SimpleActionCreator<
      TErrorActionType,
      TErrorActionProps
    >,
    collectionKey: Collection | undefined,
    getPendingMetadata: (
      requestAction?: IRequestCorrelationState
    ) => TPendingMetadata,
    getSuccessMetadata?: (
      successActionCreator: TSuccessActionProps
    ) => TSuccessMetadata,
    preDispatch?: () => void
  ): Promise<void> {
    const mapStateFn = mapStateFnBuilder(state, collectionKey);
    mapStateFn(
      (ns, key) =>
        setPendingRequestState(
          ns,
          getPendingMetadata?.(requestAction) ?? null,
          key
        ),
      (s) =>
        setPendingRequestState(s, getPendingMetadata?.(requestAction) ?? null)
    );
    preDispatch?.();
    const resultPromise = firstValueFrom(
      actions.pipe(
        ofType(successActionCreator, errorActionCreator),
        filter((action) => action.correlationId === requestAction.correlationId)
      )
    );
    store.dispatch(requestAction);
    try {
      const resultAction = await resultPromise;
      mapStateFn(
        (ns, key) => {
          if (resultAction.type === errorActionCreator.type) {
            setErrorRequestState(
              ns,
              (resultAction as TErrorActionProps).error,
              key
            );
          } else {
            setSuccessRequestState(
              ns,
              getSuccessMetadata?.(resultAction as TSuccessActionProps) ?? null,
              key
            );
          }
        },
        (s) => {
          if (resultAction.type === errorActionCreator.type) {
            setErrorRequestState(s, (resultAction as TErrorActionProps).error);
          } else {
            setSuccessRequestState(
              s,
              getSuccessMetadata?.(resultAction as TSuccessActionProps) ?? null
            );
          }
        }
      );
    } catch (e) {
      mapStateFn(
        (ns, key) => setErrorRequestState(ns, e, key),
        (s) => setErrorRequestState(s, e)
      );
    }
  }
  return startRequest;
};
